import { useEffect, useState } from "react";

/* Phone layout or desktop layout, by WIDTH.
 *
 * This briefly keyed off the device instead — `(pointer: coarse) and (hover: none)`, which
 * detects a finger rather than a window size. That is the more precise question and it is the
 * wrong one to ask, for two reasons.
 *
 * It is not reliable. A touchscreen laptop in tablet mode reports coarse and hoverless, and
 * would be locked into the phone layout on a 15" screen with no way out. An iPad in Split
 * View does the same. The spec has no answer for a stylus.
 *
 * And the failure modes are not symmetric. Width fails by giving the phone layout to someone
 * who dragged a window narrow — fixed by dragging it back. Device detection fails by trapping
 * someone in it. A recoverable wrong answer beats an unrecoverable one.
 *
 * Width also has two properties nothing else does: it agrees with every `md:` class in the
 * app, so the JS branch and the CSS can never disagree about where the line is; and it can be
 * tested by resizing a window, which is how this app is actually developed.
 *
 * AND A HEIGHT FLOOR, because width alone was wrong for a phone held sideways. Rotating one
 * makes it WIDER than 767 — iPhone 15 is 852, a Pro Max 932, a Pixel 8 915, a Galaxy S24 780
 * — so every modern phone silently switched to the desktop layout and then had to fit it into
 * 360-430px of height. Only an iPhone SE, 667 wide rotated, was narrow enough to escape. The
 * height check is what the width was always standing in for: "is there room for two columns".
 *
 * 500px is comfortably above the tallest phone in landscape (430) and far below any laptop or
 * tablet, so it separates those two cases and catches nothing else.
 *
 * THIS MUST MATCH `tailwind.config.js`, where `md` is overridden to the same pair of
 * conditions. A JS branch and a `md:` class that disagree about where the line is will render
 * half of one layout and half of the other, which is worse than either.
 */
const PHONE_QUERY = "(max-width: 767px), (max-height: 499px)";

export const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== "undefined" && window.matchMedia(PHONE_QUERY).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener("change", onChange);
    // Re-read on mount: the constructor ran during the first render, and an emulation change
    // can land between that and the effect.
    onChange();
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isPhone;
};

export default useIsPhone;
